import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  getClientLookup,
  getClientCompetitorLookup,
  getClientPresetLookup,
  getClientPositionTypeLookup,
  getClientSourceLookup,
  getClientStatusLookup,
  getClientTypeLookup,
  getCountryLookup,
  getReferenceCountries,
  getCurrencyLookup,
  getDistanceLookup,
  getFuelLookup,
  getLanguageLookup,
  getPaymentLookup,
  getPaymentSubsUnitLookup,
  getPaymentTypeLookup,
  getPositionLookup,
  getPprPaymentTermLookup,
  getProductLookup,
  getTemperatureLookup,
  getVolumeLookup,
  getWeightLookup,
  getTireMakeLookup,
} from "src/services/lookup/lookupService";
import { useGeneralRefetchOverride } from "./general.queries";
import { QueryNames } from "./queries.constant";

const LOOKUP_STALE_TIME = 60 * 60 * 1000;

const useLookupQuery = (
  queryName: readonly string[],
  queryFn: (params: Record<string, unknown>, language?: string) => Promise<unknown>,
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...queryName, params, language],
      queryFn: () => queryFn(params, language),
      enabled,
      refetchOnMount: true,
      staleTime: LOOKUP_STALE_TIME,
      retry: false,
      ...options,
    }),
    enabled
  );
};

export const useQueryGetLanguageLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.languageLookup, getLanguageLookup, params, options);

export const useQueryGetClientLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.clientLookup, getClientLookup, params, options);

export const useQueryGetClientPresetLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.clientPresetLookup, getClientPresetLookup, params, options);

export const useQueryGetClientTypeLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.clientTypeLookup, getClientTypeLookup, params, options);

export const useQueryGetClientStatusLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.clientStatusLookup, getClientStatusLookup, params, options);

export const useQueryGetCountryLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.countryLookup, getCountryLookup, params, options);

export const useQueryGetReferenceCountries = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.referenceCountries, getReferenceCountries, params, options);

export const useQueryGetTemperatureLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.temperatureLookup, getTemperatureLookup, params, options);

export const useQueryGetCurrencyLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.currencyLookup, getCurrencyLookup, params, options);

export const useQueryGetWeightLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.weightLookup, getWeightLookup, params, options);

export const useQueryGetVolumeLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.volumeLookup, getVolumeLookup, params, options);

export const useQueryGetFuelLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.fuelLookup, getFuelLookup, params, options);

export const useQueryGetDistanceLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.distanceLookup, getDistanceLookup, params, options);

export const useQueryGetPositionLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.positionLookup, getPositionLookup, params, options);

export const useQueryGetProductLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.productLookup, getProductLookup, params, options);

export const useQueryGetClientSourceLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.clientSourceLookup, getClientSourceLookup, params, options);

export const useQueryGetClientCompetitorLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.clientCompetitorLookup, getClientCompetitorLookup, params, options);

export const useQueryGetClientPositionTypeLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) =>
  useLookupQuery(QueryNames.clientPositionTypeLookup, getClientPositionTypeLookup, params, options);

export const useQueryGetPaymentTypeLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.paymentTypeLookup, getPaymentTypeLookup, params, options);

export const useQueryGetPaymentLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.paymentLookup, getPaymentLookup, params, options);

export const useQueryGetPaymentSubsUnitLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.paymentSubsUnitLookup, getPaymentSubsUnitLookup, params, options);

export const useQueryGetPprPaymentTermLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.pprPaymentTermLookup, getPprPaymentTermLookup, params, options);

export const useQueryGetTireMakeLookup = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => useLookupQuery(QueryNames.tireMakeLookup, getTireMakeLookup, params, options);
